import { EdgelessObject, type IFramework, type IResultOptions } from '@/framework';
import { getRotatePosition } from '@/utils/collision-kit';
import { createCanvas } from '@/utils/file-kit';
import { traverseTreePreOrder } from '@/utils/tree-kit';
import type { ILayerProps } from '@/version-types';
import { canUseSmallGroupDoubleScale } from './utils/result-scale';

interface IGroupRenderSource {
    layer: ILayerProps;
    object: EdgelessObject;
    renderBox: ReturnType<EdgelessObject['getBaseInfo']>;
}

interface IGroupRenderedSource extends IGroupRenderSource {
    canvas: HTMLCanvasElement;
}

export class EdgelessGroupLayer extends EdgelessObject {
    constructor(layerId: string, framework: IFramework) {
        super(layerId, framework.service, true);
    }

    /**
     * 在子图层绘制完成后叠加组合水印，组合内容由画布逐个绘制子图层
     *
     * @param ctx - 已应用组合位置、旋转和透明度的局部画布上下文
     */
    override render(ctx: CanvasRenderingContext2D) {
        if (this.width <= 0 || this.height <= 0) return;
        this.service.svipWatermark.drawLayerWatermark(this, ctx);
    }

    private getRenderSources(layer: ILayerProps) {
        const leafLayers: ILayerProps[] = [];

        traverseTreePreOrder(layer, (item: ILayerProps) => {
            if (
                item.id !== layer.id &&
                !this.service.flatLayer.checkIsGroup(item) &&
                !this.service.flatLayer.checkIsNeedHidden(item) &&
                item.width > 0 &&
                item.height > 0
            ) {
                leafLayers.push(item);
            }
        });

        const renderSources: IGroupRenderSource[] = [];

        for (const child of leafLayers) {
            const object = this.service.edgelessCanvas.getByLayerId(child.id);
            if (!object) continue;
            const renderBox = object.getBaseInfo();
            renderSources.push({ layer: child, object, renderBox });
        }

        return renderSources;
    }

    private resolveRenderBox(layer: ILayerProps, renderSources: IGroupRenderSource[]) {
        const positions = renderSources.flatMap(({ layer: child, renderBox }) =>
            getRotatePosition({ ...renderBox, rotate: child.rotate }, { x: renderBox.left, y: renderBox.top }),
        );

        if (!positions.length) {
            return {
                left: layer.left,
                top: layer.top,
                width: Math.max(1, layer.width),
                height: Math.max(1, layer.height),
            };
        }

        const minX = Math.min(...positions.map((item) => item.x));
        const minY = Math.min(...positions.map((item) => item.y));
        const maxX = Math.max(...positions.map((item) => item.x));
        const maxY = Math.max(...positions.map((item) => item.y));

        return {
            left: minX,
            top: minY,
            width: Math.max(1, maxX - minX),
            height: Math.max(1, maxY - minY),
        };
    }

    override getBaseInfo() {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        return this.resolveRenderBox(layer, this.getRenderSources(layer));
    }

    override supportsCanvasResult() {
        return true;
    }

    override async getResult({ aiWatermark = false, scale = 1 }: IResultOptions = {}) {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        const renderSources = this.getRenderSources(layer);
        const initialRenderBox = this.resolveRenderBox(layer, renderSources);
        const resolveScale = (renderBox: IGroupRenderSource['renderBox']) =>
            canUseSmallGroupDoubleScale(renderBox, scale)
                ? scale
                : this.getResourceScale({
                      logicalWidth: renderBox.width,
                      logicalHeight: renderBox.height,
                      targetScale: scale,
                  });
        const renderAtScale = async (targetScale: number) => {
            const sources: IGroupRenderedSource[] = [];
            for (const item of renderSources) {
                const result = await item.object.getResult({ aiWatermark, scale: targetScale });
                // 组合阶段统一应用 child.opacity，兜底图不预先叠加透明度。
                const fallbackCanvas = result.canvas
                    ? undefined
                    : await this.service.behavior.get(item.layer)?.getLayerImageAsync?.(item.layer, false);
                const canvas = result.canvas || fallbackCanvas;
                if (!canvas) continue;
                sources.push({
                    ...item,
                    canvas,
                    renderBox: result.canvas ? result.renderBox : item.renderBox,
                });
            }
            return sources;
        };

        let sourceScale = resolveScale(initialRenderBox);
        let renderedSources = await renderAtScale(sourceScale);
        let resultRenderBox = this.resolveRenderBox(layer, renderedSources);
        // 首次渲染可能刷新旧特效边界；实际为小图时重取 2x 子图，避免放大低分辨率结果。
        if (sourceScale < 2 && canUseSmallGroupDoubleScale(resultRenderBox, scale)) {
            sourceScale = 2;
            renderedSources = await renderAtScale(sourceScale);
            resultRenderBox = this.resolveRenderBox(layer, renderedSources);
        }

        const { left: minX, top: minY, width: logicalWidth, height: logicalHeight } = resultRenderBox;

        const effectiveScale = Math.min(sourceScale, resolveScale(resultRenderBox));
        const width = Math.max(1, Math.round(logicalWidth * effectiveScale));
        const height = Math.max(1, Math.round(logicalHeight * effectiveScale));
        const { canvas, ctx } = createCanvas(width, height);

        for (const item of renderedSources) {
            const { layer: child, renderBox, canvas: childCanvas } = item;
            ctx.save();
            ctx.translate((renderBox.left - minX) * effectiveScale, (renderBox.top - minY) * effectiveScale);
            ctx.rotate((child.rotate * Math.PI) / 180);
            ctx.globalAlpha = child.opacity;
            ctx.drawImage(
                childCanvas,
                0,
                0,
                Math.round(renderBox.width * effectiveScale),
                Math.round(renderBox.height * effectiveScale),
            );
            ctx.restore();
        }

        return { canvas, renderBox: resultRenderBox };
    }

    override async loadResource() {}
}
